// Core
import { lazy, Suspense, useEffect, useRef } from 'react';
// Packages
import moment from 'moment';
import { ConfigProvider } from 'antd';
import { BrowserRouter as Router } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import 'moment/dist/locale/vi';
import 'moment/dist/locale/pt';
import 'moment/dist/locale/es';
import { identifyLogRocketUser } from 'sessionRecording';
// Components
import { Layout } from 'components/lib';
import { Content, ErrorBoundary, SideRail } from 'components';
import { preloadTimeZoneSelect } from 'components/lib/TimeZoneSelect/TimeZoneSelect';
import { preloadPhoneInput } from 'components/lib/PhoneInput/PhoneInput';
// Custom Hooks
import { useRoutePermissions } from 'customHooks';
// Redux
import { getCurrentUserAsync } from 'redux/user/userThunk';
import { useAppDispatch, useAppSelector, STORE_KEYS } from 'redux/store';
import { getCheckpointsSettingsAsync } from 'redux/checkpoints/checkpointsThunk';
// Modules
import storage from 'modules/storage';
// Helpers
import prefetchWhenIdle from 'helpers/prefetchWhenIdle';
// Routes
import { AppRouter, preloadRoute, preloadOnIntent } from 'router';
import routes, { ROUTES } from 'router/routes';
// Configs
import dateConfig from 'configs/dateConfig';
// Constants
import {
  DocumentVisibilityState,
  SUBSCRIPTION_REFRESH_DEBOUNCE_MS,
} from './helpers/data/constants';
// I18n
import {
  TLang,
  getCurrentLanguage,
  getCurrentLocale,
  getLocaleOptions,
  languages,
  changeLanguage,
  TLangKeys,
} from 'i18n';

// The signed-in header (settings modal, uploaders, tables) is not part of the sign-in, sign-up and
// recover-password pages. A returning user's session is already in storage, so its chunk starts
// downloading with the entry, in parallel with the page's, not after the first render.
const loadHeader = () => import('components/Header');
const Header = lazy(loadHeader);

// The router renders no private page until /users/me has answered, and only then asks for the page's
// chunk (and its tab's). Starting them now makes them download while that request is in flight.
if (storage.getData()?.accessToken) {
  void loadHeader();
  preloadRoute(window.location.pathname);
}

const App = () => {
  const dispatch = useAppDispatch();

  const { user } = useAppSelector(STORE_KEYS.USER);
  const { isLoggedIn, userId } = useAppSelector(STORE_KEYS.AUTH);
  const isAuth = !!isLoggedIn && !!userId;
  const allowedRoutes = useRoutePermissions(routes);

  useEffect(() => preloadOnIntent(), []);

  // The timezone and phone fields of the modals load on their own; this fetches them in the background
  // once the app is idle, so the first modal does not wait for them.
  useEffect(
    () => (isAuth ? prefetchWhenIdle([preloadTimeZoneSelect, preloadPhoneInput]) : undefined),
    [isAuth],
  );

  // Tags the LogRocket session with the signed-in user
  useEffect(() => {
    if (!user) {
      return;
    }

    identifyLogRocketUser(String(user?.id), {
      name: `${user.firstName} ${user.lastName}`,
      email: user?.email || '-',
    });
  }, [user]);

  useEffect(() => {
    isAuth && dispatch(getCurrentUserAsync());
  }, [dispatch, isAuth]);

  // Refresh user (including subscription) when app comes to foreground so Premium shows after upgrade on web
  const lastVisibilityChangeRef = useRef<number>(0);
  useEffect(() => {
    if (!isAuth) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== DocumentVisibilityState.Visible) {
        return;
      }
      const now = Date.now();
      if (now - lastVisibilityChangeRef.current < SUBSCRIPTION_REFRESH_DEBOUNCE_MS) {
        return;
      }
      lastVisibilityChangeRef.current = now;
      dispatch(getCurrentUserAsync());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch, isAuth]);

  // need for hiding/opening this page depends on flag in checkpoints page
  useEffect(() => {
    if (user && allowedRoutes?.some(route => route.path === ROUTES.CHECKPOINTS)) {
      dispatch(getCheckpointsSettingsAsync());
    }

    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const language = storage.getData<TLang>('timeGoLang')?.lng;
    const browserLanguage = navigator.language.split('-')[0];

    if (language && language in languages) {
      return;
    }

    let appLanguage = 'en';

    if (browserLanguage in languages) {
      appLanguage = browserLanguage;
    }

    return changeLanguage(appLanguage as TLangKeys);
  }, []);

  useEffect(() => {
    if (user) {
      const currentLanguage = getCurrentLanguage();
      const localeOptions = getLocaleOptions(user?.payroll?.startDay);

      dateConfig.configureTimeFormat(user?.timeFormat);

      moment.updateLocale(currentLanguage, localeOptions);
    }
  }, [user]);

  const currentLocale = getCurrentLocale();

  return (
    <ConfigProvider locale={currentLocale}>
      <Router>
        <Layout style={{ height: '100%' }}>
          {/* The routes have their own boundary; this one catches the header and the drag-and-drop
              provider, which would otherwise unmount the whole app (white page) */}
          <ErrorBoundary>
            {isAuth && (
              <Suspense fallback={null}>
                <Header />
              </Suspense>
            )}
            <SideRail isAuth={isAuth} />
            <DndProvider backend={HTML5Backend}>
              <Content isAuth={isAuth} user={user}>
                <AppRouter />
              </Content>
            </DndProvider>
          </ErrorBoundary>
        </Layout>
      </Router>
    </ConfigProvider>
  );
};

export default App;
